<template>
  <section class="data-table">
    <div class="container-fluid">

      <div class="row">
        <div class="col-12">
          <b-alert variant="danger" :show="!!error" @dismissed="error = false" dismissible>{{ error }}</b-alert>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <b-alert variant="info" :show="!!info" @dismissed="info = false" dismissible v-html="info">
          </b-alert>
        </div>
      </div>

      <div class="row">
        <div class="col-6">
          <b-form-input :class="{'is-invalid': no_q}"  @keyup.enter="subset" v-model="q" placeholder="Search term..." size="sm"></b-form-input>
        </div>
        <div class="col-6">
          <b-form-select :class="{'is-invalid': no_field}" v-model="field" :options="fields" size="sm">
            <template #first>
              <b-form-select-option :value="null" disabled>-- Select a field --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mt-2">
          <b-button @click="subset" size="sm" variant="outline-primary">Query a subset</b-button>
          <b-button @click="openInSNPense" class="ml-2" size="sm" variant="outline-info">Open RSIDs in <strong>g:SNPense</strong></b-button>

          <b-button @click="clear" class="ml-4" size="sm" variant="outline-secondary">Clear</b-button>

            <div>
                Presets:
                <b-badge href="?gene_name=CD33" variant="secondary"><span class="badge-prefix">gene name | </span>CD33</b-badge>
                &nbsp;
                <b-badge href="?rsid=rs2927608" variant="secondary"><span class="badge-prefix">rsid | </span>rs2927608</b-badge>
                &nbsp;
                <b-badge href="?variant=chr1_232533485_C_T" variant="secondary"><span class="badge-prefix">variant | </span>chr1_232533485_C_T</b-badge>
                &nbsp;
                <b-badge href="?credible_set=QTD000234_ENSG00000186104.grp_1.contained.ENST00000334636_L1" variant="secondary"><span class="badge-prefix">credible set | </span>CYP2R1</b-badge>
            </div>
        </div>
      </div>

      <hr>

      <div class="row">
        <div class="col-12">

          <div v-if="loader" class="text-center">
            <div class="spinner-border text-primary spinner" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>

          <ag-grid-vue domLayout='autoHeight'
                       class="ag-theme-balham"
                       :pagination="true"
                       :gridOptions="gridOptions"
                       @grid-ready="onGridReady"
                       @modelUpdated="onModelUpdated"
                       @rowSelected="onRowSelected"
                       :frameworkComponents="frameworkComponents"
                       :context="{ performCredibleSetSearch: this.performCredibleSetSearch }"
                       :columnDefs="columnDefs"
                       :defaultColDef="defaultColDef"
                       rowSelection="single"
                       :paginationPageSize="10"
                       :rowData="rowData"
                       ref="agGrid">
          </ag-grid-vue>
          
            <div v-if="loader_plots" class="text-center mt-4">
                <div class="spinner-border text-primary spinner" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
            <div class="flex-row">
      <div class="flex-column-6">
        <coverage-plot :input="coverage" :meta="meta" :lineCoordinates="lineCoordinates"></coverage-plot>
        <exon-sumstats-plot :input="exon_sumstats_input" :meta="meta" :lineCoordinates="lineCoordinates"></exon-sumstats-plot>
        <div class="annotations-wrap">
          <transcript-anno-plot 
            :input="transcript_anno" 
            :meta="meta" 
            :lineCoordinates="lineCoordinates"
            @annoSelected="handleAnnoSelected" 
            @newLineCoordinates="handleNewLineCoordinates"></transcript-anno-plot>
        </div>
      </div>
      <div class="flex-column-4">
        <div class="comment-widget-container">
        <comment-widget v-show="isAuthenticated" @commentAdded="handleCommentAdd" ></comment-widget>
        </div>
        <box-plot 
        :input="boxplot" 
        :meta="meta" 
        :selectedAnno="selectedAnno"
        ></box-plot>
      </div>
    </div>
          </div>
      </div>
    </div>


</section>
</template>

<script>

import CoveragePlot from "@/components/CoveragePlot.vue";
import ExonSumstatsPlot from "@/components/ExonSumstatsPlot.vue";
import TranscriptAnnoPlot from "@/components/TranscriptAnnoPlot.vue";
import BoxPlot from "@/components/BoxPlot.vue";
import CommentWidget from "@/components/CommentWidget.vue";


import {AgGridVue} from "ag-grid-vue";
import CredibleSetButtonRenderer from '@/components/CredibleSetButtonRenderer.vue';
import { mapActions, mapGetters } from 'vuex';


import EventBus from '../event-bus';
import { AuthError } from '@/custom-errors.js';

import API from '../api';

import _ from 'lodash';

export default {
  name: 'DataTable',
  components: {
    AgGridVue, CoveragePlot, ExonSumstatsPlot, TranscriptAnnoPlot, BoxPlot, CredibleSetButtonRenderer, CommentWidget
  },
  data() {
    return {
      error: null,
      info: null,

      loader: true,
      loader_plots: false,

      data: null,
      
      lineCoordinates: null, 
      
      q: null,
      field: null,

      no_q: false,
      no_field: false,

      columns: [],
      rowData: [],
      gridApi: null,
      columnApi: null,
      frameworkComponents: null,

      defaultColDef: {
        resizable: true,
        sortable: true
      },
      gridOptions: {
        rowHeight: 23,
        rowSelection: 'single',  // Allow single row selection
      },
      columnDefs: [
        {
          headerName: 'molecular trait id',
          field: 'molecular_trait_id',
          width: 150,
          minWidth: 150,
            filter: true,
        },
        {
          headerName: 'gene name',
          field: 'gene_name',
          width: 100,
            filter: true,
        },
        {
          headerName: 'gene id',
          field: 'gene_id',
          width: 100,
          filter: true,
        },
        {
          headerName: 'credible set',
          field: 'credible_set',
          width: 180,
          filter: true,
          cellRendererFramework: 'CredibleSetButtonRenderer', 
        },
        {
          headerName: 'variant',
          field: 'variant',
          width: 180,
            filter: true,
        },
        {
          headerName: 'rs id',
          field: 'rsid',
          width: 100,
            filter: true,
        },
          {
              headerName: 'quantification method',
              field: 'quantification_method',
              width: 100,
              filter: true,
          },
        {
          headerName: 'credible set size',
          field: 'credible_set_size',
          width: 50,
            filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'pip',
          field: 'pip',
          width: 100,
            filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'p value',
          field: 'pvalue',
          width: 100,
            filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'beta',
          field: 'beta',
          width: 100,
            filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'se',
          field: 'se',
          width: 100,
            filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'dataset',
          field: 'dataset',
          width: 150,
            filter: true,
        },
          {
              headerName: 'dataset id',
              field: 'dataset_id',
              width: 150,
              filter: true,
          },
          {
            headerName: 'dataset label',
            field: 'dataset_label',
            width: 150,
          },
          {
              headerName: 'plot variant',
              field: 'plot_variant',
              width: 75,
          },
      ],

      searchable_fields: ['molecular_trait_id', 'gene_name', 'credible_set', 'variant', 'rsid'],
      coverage: null,
      exon_sumstats_input: null,
      exon_sumstats: null,
      transcript_anno: null,
      boxplot: null,
      meta: null,
      selectedAnno: null,
    }
  },
  watch: {
    $route(to, from) {
      this.check_q();
    },
    field() {
      this.no_field = false;
    },
    q() {
      this.no_q = false;
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    fields() {
      return _.filter(_.map(this.columnDefs, col => {
        return {
          value: col.field,
          text: col.headerName
        };
      }), field => this.searchable_fields.includes(field.value));
    },
    rs_ids() {
      return _.map(this.rowData, record => {
        return record.rsid;
      })
    },
    vertical_line_coordinates(){
      let cds = this.transcript_anno.feature_type.map((d,i) => {
        let f = this.transcript_anno.molecular_trait_ids[i]
        if (d === "cds" && !f.includes("GENE:")) {
          let temp_obj = {
            start: this.transcript_anno.start[i],
            end: this.transcript_anno.end[i]
          }
          return temp_obj
        }
      }).filter(item => item !== undefined);
      return cds
    },
  },
  mounted() {

    this.check_q();

    window.addEventListener("resize", _.throttle(()=>{
      this.onModelUpdated();
    }), 300);
   
  },
  methods: {
    check_q() {

      if (this.$route.query.molecular_trait_id) {
        this.field = 'molecular_trait_id';
        this.q = this.$route.query.molecular_trait_id;
        this._subset();
      } else if (this.$route.query.gene_name) {
        this.field = 'gene_name';
        this.q = this.$route.query.gene_name;
        this._subset();
      } else if (this.$route.query.credible_set) {
        this.field = 'credible_set';
        this.q = this.$route.query.credible_set;
        this._subset();
      } else if (this.$route.query.rsid) {
        this.field = 'rsid';
        this.q = this.$route.query.rsid;
        this._subset();
      } else if (this.$route.query.variant) {
        this.field = 'variant';
        this.q = this.$route.query.variant;
        this._subset();
      } else {
        this.field = null;
        this.q = null;
        this._page();
      }

    },
    performCredibleSetSearch(credibleSetValue) {
      this.field = 'credible_set';
      this.q = credibleSetValue;
      this._subset();

      this.$router.push({
          query: { [this.field]: this.q }
        }).catch(err => {});
    },
    _subset() {
      this.loader = true;
      API.subset(this.q, this.field, json=>{
        this.rowData = json.page;

        this.info = `Found <strong>${json.n}</strong> records based on the specified criteria: <strong>${json.q} [${json.field}]</strong>.`;

        this.loader = false;
      });
      // TO DO clear comment widget
    },
    _page() {
      this.loader = true;
      API.page(0, 100, json=>{
        // console.log(json.page);

        this.rowData = json.page;

        this.info = `Loaded <strong>${json.limit}</strong> (out of <strong>${json.total}</strong> in total) records for preview purposes.`;
        this.loader = false;
      });
    },
    clear() {
      this.error = '';
      this.no_q = false;
      this.no_field = false;

      let query = {};
      this.$router.push({
        query: query
      }).catch(err => {});
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      this.gridApi.sizeColumnsToFit();

        params.api.addEventListener("firstDataRendered", () => {
            const columnState = params.columnApi.getColumnState();
            const sortColumn = columnState.find(col => col.sort);

            if (sortColumn && sortColumn.colId === "plot_variant" && sortColumn.sort === "desc") {
                const firstRowNode = this.gridApi.getDisplayedRowAtIndex(0);
                if (firstRowNode) {
                    firstRowNode.setSelected(true);
                }
            }
        });

        let columnState = params.columnApi.getColumnState();
        let sortColumn = columnState.find(col => col.sort);

        if (!sortColumn) {
            params.columnApi.applyColumnState({
              
                state: [
                    { colId: 'plot_variant', sort: 'desc' }
                ],
                defaultState: { sort: null }
            });
        }
    },
    onModelUpdated() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    handleAnnoSelected(data) {
      this.selectedAnno = data;
    },
    handleNewLineCoordinates(data) {
      this.lineCoordinates[0].start = data[0].start;
      this.lineCoordinates[0].end = data[0].end;
      this.lineCoordinates[1].start = data[1].start;
      this.lineCoordinates[1].end = data[1].end;
    },
    ...mapActions(['updateCommentDetail', 'clearCommentDetails', 'logout', 'setCommentDetails', 'updateResponseMessage']),
    onRowSelected(event) {
      if (event.node.selected) {
        // We only get comments if the user is admin, that is why checking for authentication here.
        if (this.isAuthenticated) {
          let rowData = event.node.data;
        API.getComment(rowData.credible_set, rowData.molecular_trait_id)
        .then(data => {
            if (data === null) {
                this.clearCommentDetails();
            } else {
                this.setCommentDetails(data);
                this.updateResponseMessage({ message: '' , type: ''});
            }
        })
        .catch(error => {
              if (error instanceof AuthError) {
                this.logout();
                this.clearCommentDetails();
                this.$router.push({name: 'Login'});
                this.updateResponseMessage({ message: "Session expired, please log in", type: 'info' });
                
            } 
            else {
              this.updateResponseMessage(error);
            }
        });
        };

        this.selectedAnno = null;
        
        this.loader_plots = false;
        
        this.coverage = null;
        this.exon_sumstats = null;
        this.transcript_anno = null;
        this.exon_sumstats_input = null;
        this.boxplot = null;
        this.meta = null;
        if ( event.node.data.plot_variant ) {
          this.loader_plots = true;
          
          API.plot_data(
          event.node.data.dataset_id,
          event.node.data.gene_id,
          event.node.data.molecular_trait_id,
          event.node.data.plot_variant, // changed from `variant` to `plot_variant` to request correct plot from the server
          json=>{
            
            this.coverage = json.coverage;
            this.exon_sumstats = json.exon_sumstats;
            this.transcript_anno = json.transcript_anno;
            this.exon_sumstats_input = {exon_sumstats: json.exon_sumstats, transcript_anno: json.transcript_anno};
            this.boxplot = json.boxplot;
            this.meta = json.meta;
            this.meta["quant_method"] = event.node.data.quantification_method;
            this.loader_plots = false;
            this.lineCoordinates = this.vertical_line_coordinates
          });


        }
      }
    },
    

    subset() {  
      if (this.q && this.field) {
        this.error = '';
        this.no_q = false;
        this.no_field = false;

        let query = {};
        query[this.field] = this.q;

        this.$router.push({
          query: query
        }).catch(err => {
        });

      } else {
        this.info = '';
        if (!this.q && !this.field) {
          this.error = 'Please specify a term and select a field to query a subset by.';
          this.no_q = true;
          this.no_field = true;
        } else if (!this.q) {
          this.error = `Please specify a term to query a subset by.`;
          this.no_q = true;
        } else if (!this.field) {
          this.error = `Please select a field to query a subset by.`;
          this.no_field = true;
        }
      }

    },


    openInSNPense() {
      fetch("https://biit.cs.ut.ee/gplink/l", {
        "headers": {
          "accept-language": "en-US,en;q=0.9,ru;q=0.8,tr;q=0.7",
          "content-type": "application/json",
        },
        "body": JSON.stringify({
          url: "https://biit.cs.ut.ee/gprofiler/snpense",
          payload: {
            query: _.uniq(this.rs_ids).join('\n'),
          }
        }),
        "method": "POST",
      }).then((response) => {
        return response.json();
      }).then((json)=>{
        window.open(`https://biit.cs.ut.ee/gplink/l/${json.result}`, '_blank');
      }).catch(function (err) {
        console.log('Fetch problem: ' + err.message);
      });

    },
    handleCommentAdd() {
      const selectedNodes = this.gridApi.getSelectedNodes();
      
      if (selectedNodes.length > 0 ) {
        const selectedData = selectedNodes.map(node => node.data)[0];
        this.updateCommentDetail({ key: 'credible_set', value: selectedData.credible_set });
        this.updateCommentDetail({ key: 'molecular_trait_id', value: selectedData.molecular_trait_id});
        this.updateCommentDetail({ key: 'variant', value: selectedData.variant});
        API.comment()
        .then(data => {
                this.updateResponseMessage({ message: data.msg, type: 'success' });
                this.updateCommentDetail({ key: 'user_name', value: data.username });
        })
        .catch(error => {
            if (error instanceof AuthError) {
                this.logout();
                this.clearCommentDetails();
                this.$router.push({name: 'Login'});
                this.updateResponseMessage({ message: "Session expired, please log in", type: 'info' });

            } else {
              this.updateResponseMessage(error);
            }
        });
        };
      }
    },

  }

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.spinner {
  margin-bottom: 12px;
}
.data-table {
  overflow-x: auto;
  min-width: 1800px; /* Adjust based on your minimum layout width */
}
.flex-row {
  display: flex;
  /* flex-wrap: wrap; */
  flex-wrap: nowrap;
  gap: 20px;
}
.flex-column-6 {
  flex: 0 0 60%;
  max-width: 60%;
  display: flex;
  flex-direction: column;
}

.flex-column-4 {
  flex: 0 0 40%;
  max-width: 40%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100vh; /* Ensure it takes the full height of the viewport */
  gap: 40px;
  padding: 20px; /* Add padding to the container */
  box-sizing: border-box;
}
.annotations-wrap {
  margin-bottom: auto; /* Ensures it stays at the bottom of its column */
}



</style>

<style>

.comment-widget-container {
  flex: 0 0 auto; /* Allow the element to take its content height */
  height: 300px; /* Set to the typical height of your comment-widget */
  width: 100%;
}
.comment-widget .event-form {
  min-height: 200px; /* Ensure the component has a minimum height */
  width: 900px; /* Set width */
  padding: 10px; /* Set padding */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  border-radius: 8px;
  margin: 10px; /* Set margin */
}
.badge-prefix {
    font-weight: 100;
}


</style>
